export type Category = {
  slug: string;
  name: string;
  tagline: string;
  desc: string;
  icon: string;
  stats: number;
  image: string;
};

export type HotEvent = {
  slug: string;
  title: string;
  category: string;
  categoryName: string;
  duration: string;
  views: number;
  time: string;
  location: string;
  thumb: string;
  tags: string[];
  summary: string;
  content: string[];
};

export const categories: Category[] = [
  {
    slug: "scene",
    name: "现场实拍",
    tagline: "路人镜头下的第一现场",
    desc: "第一时间抵达冲突与争议发生的真实场景，以路人镜头完整还原现场氛围与关键细节，让事件真相不被剪辑和滤镜覆盖。",
    icon: "ri-camera-lens-line",
    stats: 1280,
    image: "https://readdy.ai/api/search-image?query=dark%20moody%20urban%20street%20corner%20at%20dusk%20with%20crowd%20silhouettes%20gathered%20around%20an%20incident%2C%20handheld%20phone%20video%20frame%2C%20motion%20blur%2C%20high%20contrast%20orange%20street%20lights%2C%20gritty%20cinematic%20documentary%20photography&width=640&height=800&seq=cat-scene-01&orientation=portrait",
  },
  {
    slug: "collection",
    name: "事件合集",
    tagline: "同一主题，多起事件纵向盘点",
    desc: "将同类热点事件按主题归拢成系列合集，从发生、发酵到各方回应完整串联，帮你看清事件脉络与背后逻辑。",
    icon: "ri-play-list-2-line",
    stats: 860,
    image: "https://readdy.ai/api/search-image?query=overhead%20view%20of%20a%20dark%20editorial%20workspace%20with%20video%20timeline%20screens%20and%20press%20clippings%20on%20a%20desk%2C%20warm%20orange%20accent%20lighting%2C%20cinematic%20depth%20of%20field%2C%20documentary%20newsroom%20aesthetic&width=640&height=800&seq=cat-collection-02&orientation=portrait",
  },
  {
    slug: "bystander",
    name: "路人视角",
    tagline: "普通人的第一手记录",
    desc: "来自现场路人的随机视角，不追求完美构图，只为保留最真实的瞬间。一次抬手、一次转身，都可能是事件最直接的证据。",
    icon: "ri-eye-line",
    stats: 1020,
    image: "https://readdy.ai/api/search-image?query=pov%20view%20of%20a%20person%20holding%20a%20phone%20amidst%20a%20dense%20night%20crowd%20on%20a%20lit%20street%2C%20shallow%20depth%20of%20field%2C%20dark%20teal%20and%20orange%20tones%2C%20candid%20documentary%20style%2C%20realistic%20handheld%20shot&width=640&height=800&seq=cat-bystander-03&orientation=portrait",
  },
  {
    slug: "dialogue",
    name: "对话录音",
    tagline: "逐字还原现场对话",
    desc: "以现场对话为核心，将争执、协商、澄清的原声一字一句还原成文字与影像，帮助判断矛盾起因与责任边界。",
    icon: "ri-voiceprint-line",
    stats: 540,
    image: "https://readdy.ai/api/search-image?query=close%20up%20of%20two%20people%20in%20heated%20conversation%20on%20a%20dim%20urban%20street%20at%20night%2C%20dramatic%20warm%20side%20light%2C%20dark%20background%2C%20documentary%20photography%2C%20emotional%20tension%2C%20cinematic%20grain&width=640&height=800&seq=cat-dialogue-04&orientation=portrait",
  },
  {
    slug: "record",
    name: "全程记录",
    tagline: "从开始到结束，完整不中断",
    desc: "对重大热点事件进行不间断全程记录，从矛盾萌芽到现场平息，一条视频看完整起经过，最大程度还原事件原貌。",
    icon: "ri-record-circle-line",
    stats: 720,
    image: "https://readdy.ai/api/search-image?query=wide%20dark%20urban%20square%20at%20night%20with%20a%20continuous%20crowd%20gathering%20and%20spotlights%2C%20long%20exposure%20sense%2C%20orange%20and%20amber%20city%20glow%2C%20documentary%20surveillance%20camera%20style%2C%20cinematic&width=640&height=800&seq=cat-record-05&orientation=portrait",
  },
];

export const hotEvents: HotEvent[] = [
  {
    slug: "metro-scene-20260816",
    title: "地铁车厢突发争执 路人全程实录还原现场",
    category: "scene",
    categoryName: "现场实拍",
    duration: "03:42",
    views: 582000,
    time: "2026-08-16",
    location: "3号线 · 某市中心站",
    thumb: "https://readdy.ai/api/search-image?query=inside%20a%20dimly%20lit%20subway%20carriage%20at%20night%20with%20two%20people%20in%20a%20heated%20argument%20near%20the%20doors%2C%20other%20passengers%20turning%20to%20watch%2C%20handheld%20phone%20video%20frame%2C%20motion%20blur%2C%20cool%20blue%20and%20warm%20orange%20lighting%2C%20candid%20documentary&width=640&height=360&seq=ev-metro-01&orientation=landscape",
    tags: ["地铁", "冲突", "现场实拍"],
    summary: "晚高峰时段，某地铁车厢内两名乘客因拥挤推搡发生激烈争执，路人全程用手机记录下事发经过，视频还原了从口角升级到僵持的完整过程。",
    content: [
      "事发于晚高峰时段的3号线列车上，车厢内人潮拥挤。据现场路人描述，一名乘客在关门瞬间急于上车，与门边的乘客发生肢体推搡，双方随即在车厢内爆发激烈口角，言语中多次互相指责对方‘先动的手’。",
      "现场拍摄的视频显示，争执持续约四分钟，期间周围乘客数次试图上前劝架，但均被情绪激动的双方打断。列车到站后，工作人员上车进行调解，事件才逐渐平息。不少网友在评论区表示，公共场所纠纷最怕的是‘各说各话’，而这类全程实录恰恰提供了客观参照。",
    ],
  },
  {
    slug: "parking-dispute-20260814",
    title: "小区停车位纠纷实录 物业调解对话曝光",
    category: "bystander",
    categoryName: "路人视角",
    duration: "05:18",
    views: 431000,
    time: "2026-08-14",
    location: "城东 · 某小区地下车库",
    thumb: "https://readdy.ai/api/search-image?query=dark%20underground%20parking%20garage%20with%20two%20car%20owners%20arguing%20beside%20their%20vehicles%2C%20fluorescent%20ceiling%20lights%2C%20concrete%20pillars%2C%20candid%20bystander%20video%20frame%2C%20high%20contrast%2C%20documentary%20photography&width=640&height=360&seq=ev-parking-02&orientation=landscape",
    tags: ["停车", "物业", "路人视角"],
    summary: "两名业主因固定车位被临时占用发生争执，物业人员到场后三方对峙近十分钟。围观邻居拍下的视频完整记录了调解全过程。",
    content: [
      "视频拍摄于城东某小区地下车库，一名业主发现自己的固定车位被陌生车辆占用，情急之下通过业主群找到了占用车辆的车主。双方到场后各执一词，一位认为‘车位私有’，另一位则强调‘临时办事停一下’。",
      "物业值班人员随后赶到，在中间反复协调，最终以占用车辆主动挪车并道歉收场。多位邻居在群内感叹，公共车位管理缺乏明确规则，是这类纠纷反复出现的根源，希望物业能尽快完善登记制度。",
    ],
  },
  {
    slug: "mall-queue-20260815",
    title: "商场排队插队风波 收银台前现场实拍",
    category: "scene",
    categoryName: "现场实拍",
    duration: "02:57",
    views: 356000,
    time: "2026-08-15",
    location: "万象城 · 负一层超市",
    thumb: "https://readdy.ai/api/search-image?query=supermarket%20checkout%20counter%20with%20a%20long%20queued%20line%20and%20two%20shoppers%20in%20an%20argument%20at%20the%20front%2C%20bright%20store%20lighting%2C%20other%20customers%20watching%2C%20handheld%20video%20frame%2C%20realistic%20documentary&width=640&height=360&seq=ev-mall-03&orientation=landscape",
    tags: ["商场", "排队", "现场实拍"],
    summary: "超市晚市结账高峰，一名顾客试图从队伍前方‘见缝插针’被后排顾客当场指出，双方在收银台前发生争执，场面一度引发现场围观。",
    content: [
      "事发在商场负一层超市的晚市结账时段，收银台前排起长龙。一名顾客拎着少量商品，试图直接绕过队伍走到收银台前，被一位排了许久的顾客当场叫住，质疑其‘插队’。",
      "拍摄视频显示，被指插队的顾客解释‘只是赶时间少买了点东西’，而排队顾客则认为规则面前人人平等。双方争执约三分钟，最终在超市工作人员协调下按顺序完成结账。此事也再次引发关于公共场所排队礼仪的讨论。",
    ],
  },
  {
    slug: "night-market-20260812",
    title: "夜市摊位抢客纠纷 摊主对话录音逐字还原",
    category: "dialogue",
    categoryName: "对话录音",
    duration: "08:05",
    views: 689000,
    time: "2026-08-12",
    location: "城西 · 老字号夜市",
    thumb: "https://readdy.ai/api/search-image?query=night%20market%20food%20stalls%20at%20night%20with%20glowing%20warm%20lanterns%20and%20two%20stall%20owners%20arguing%20in%20front%20of%20their%20booths%2C%20steam%20and%20smoke%20rising%2C%20crowd%20gathering%2C%20cinematic%20documentary%20photography&width=640&height=360&seq=ev-market-04&orientation=landscape",
    tags: ["夜市", "摊贩", "对话录音"],
    summary: "两家相邻摊位因‘拉客抢单’发生冲突，现场路人录下全程对话并整理成文字。争执围绕经营范围与经营边界展开，各说各理。",
    content: [
      "两家相邻的烧烤与炸串摊位，因一名顾客在两摊前犹豫‘被抢走’而爆发矛盾。现场录音还原了双方从‘你这是抢我的客’到互相指责对方‘越界经营’的完整对话，情绪逐渐升温。",
      "多位夜市常客表示，这类摊位间的边界纠纷时有发生，根源在于夜市摊位区域划分不清晰。当晚城管人员到场后，双方在管理部门的协调下划定临时分界，事态才得以平息。",
    ],
  },
  {
    slug: "highway-service-20260813",
    title: "高速服务区突发争执 全程记录真相还原",
    category: "record",
    categoryName: "全程记录",
    duration: "12:30",
    views: 745000,
    time: "2026-08-13",
    location: "G15 高速 · 某服务区",
    thumb: "https://readdy.ai/api/search-image?query=highway%20rest%20service%20area%20at%20dusk%20with%20two%20travelers%20arguing%20near%20parked%20cars%20and%20a%20fuel%20station%2C%20wide%20shot%2C%20amber%20highway%20lights%2C%20open%20sky%2C%20documentary%20surveillance%20style%2C%20cinematic&width=640&height=360&seq=ev-highway-05&orientation=landscape",
    tags: ["高速", "服务区", "全程记录"],
    summary: "因停车位占用与加油排队问题，两名长途车主在服务区发生争执。整段12分半的视频不间断记录了从争吵到和解的完整经过。",
    content: [
      "假期返程高峰，G15高速某服务区车辆爆满。两名车主因临时车位被占、且后方车辆加油排队次序不清而发生口角，情绪激动到一度需要旁人拉开。",
      "值得关注的是，一段不间断的全程记录显示，双方冷静后主动握手言和，并互相致歉‘都是赶路太累、火气大了’。不少网友以此为例表示，遇到纠纷先冷静三分钟，远比争个输赢重要。",
    ],
  },
  {
    slug: "delivery-dispute-20260811",
    title: "外卖小哥与商家纠纷 取餐现场实拍引热议",
    category: "scene",
    categoryName: "现场实拍",
    duration: "04:09",
    views: 398000,
    time: "2026-08-11",
    location: "大学城 · 某餐饮街",
    thumb: "https://readdy.ai/api/search-image?query=delivery%20rider%20with%20a%20delivery%20bag%20arguing%20with%20a%20restaurant%20staff%20at%20the%20counter%20of%20a%20busy%20street%20restaurant%20at%20night%2C%20neon%20signs%2C%20handheld%20video%20frame%2C%20motion%20blur%2C%20documentary%20photography&width=640&height=360&seq=ev-delivery-06&orientation=landscape",
    tags: ["外卖", "商家", "现场实拍"],
    summary: "因出餐慢导致订单超时，外卖骑手与商家在取餐口发生争执。现场视频还原了‘互相埋怨超时扣款’的矛盾焦点。",
    content: [
      "正值午间送餐高峰，大学城一家餐饮店出餐速度跟不上订单量，一名骑手因长时间取不到餐而与店员发生争执。骑手称‘超时被扣钱’，店员则委屈表示‘人手上不来也没办法’。",
      "拍摄视频引发大量从业者共鸣，评论区里骑手和商家互相吐槽，也有人建议平台应优化派单与出餐的数据联动。当晚，该店负责人公开回应将加派人手，并向当班骑手致歉。",
    ],
  },
  {
    slug: "square-dance-20260810",
    title: "广场舞音量之争 居民与舞队现场对话",
    category: "dialogue",
    categoryName: "对话录音",
    duration: "06:22",
    views: 512000,
    time: "2026-08-10",
    location: "滨江公园 · 中心广场",
    thumb: "https://readdy.ai/api/search-image?query=public%20park%20square%20at%20dusk%20with%20a%20group%20of%20dancers%20near%20a%20speaker%20and%20a%20resident%20confronting%20them%2C%20evening%20sky%2C%20city%20park%20lights%2C%20documentary%20photography%2C%20cinematic%20warm%20tones&width=640&height=360&seq=ev-square-07&orientation=landscape",
    tags: ["广场舞", "噪音", "对话录音"],
    summary: "晚上九点后广场舞音乐依旧响亮，周边居民下楼沟通未果，双方在广场上展开一段各执一词的现场对话，被路人录下。",
    content: [
      "滨江公园中心广场每晚的广场舞一直持续到深夜，音量问题让周边高层住户颇为困扰。当晚，一位居民带着分贝记录下楼交涉，与领队就‘九点后应降低音量’展开对话。",
      "录音还原了双方观点：舞队强调‘我们也有娱乐权利，音量不大怎么听得清’，居民则反驳‘你的娱乐不该建立在别人休息之上’。最终在社区工作人员协调下，双方约定高峰时段调低音量并提前收场，给同类矛盾提供了一个解决样本。",
    ],
  },
  {
    slug: "bus-yield-20260809",
    title: "公交车让座风波 车厢内现场实拍全记录",
    category: "bystander",
    categoryName: "路人视角",
    duration: "03:15",
    views: 287000,
    time: "2026-08-09",
    location: "18路 · 开往城北",
    thumb: "https://readdy.ai/api/search-image?query=inside%20a%20city%20public%20bus%20with%20rows%20of%20seats%20and%20passengers%2C%20an%20elderly%20passenger%20and%20a%20young%20passenger%20in%20a%20discussion%20about%20a%20seat%2C%20daylight%20through%20windows%2C%20documentary%20video%20frame%2C%20realistic&width=640&height=360&seq=ev-bus-08&orientation=landscape",
    tags: ["公交", "让座", "路人视角"],
    summary: "一名老人因无人主动让座当众抱怨，一位年轻乘客起身回应‘我刚才也想让，但您没开口’。车厢内的这段对话引发全网关于‘让座礼仪’的讨论。",
    content: [
      "18路公交车行驶途中，一名老人因长时间站着无人让座，情绪有些激动地开口抱怨‘现在年轻人都不让座了’。话音刚落，一位本已准备起身的年轻乘客回应道，自己一直在观察，只是老人没有主动示意。",
      "视频走红后，关于‘让座到底是义务还是情分’的讨论愈发热烈。多数网友认为，让座是善举而非强制，相互体谅、有需要主动开口，才是公共空间相处最舒服的方式。",
    ],
  },
  {
    slug: "grocery-weight-20260808",
    title: "菜市场称重纠纷 商户与顾客现场对质",
    category: "scene",
    categoryName: "现场实拍",
    duration: "04:44",
    views: 265000,
    time: "2026-08-08",
    location: "城北 · 综合农贸市场",
    thumb: "https://readdy.ai/api/search-image?query=busy%20open%20air%20vegetable%20market%20stall%20with%20a%20vendor%20and%20a%20customer%20arguing%20over%20a%20scale%20and%20plastic%20bag%20of%20produce%2C%20morning%20light%2C%20crowd%20watching%2C%20documentary%20photography%2C%20cinematic&width=640&height=360&seq=ev-grocery-09&orientation=landscape",
    tags: ["菜市场", "称重", "现场实拍"],
    summary: "顾客怀疑商品称重不准，拿出手机复称后与摊主当面对质。视频记录了双方从质疑、辩解到达成退款的完整过程。",
    content: [
      "城北综合农贸市场里，一名顾客在购买水果后觉得分量偏轻，便在市场公平秤处复称，结果与摊主称的重量存在出入。顾客拿着复称结果回到摊位理论，摊主起初坚持‘称是准的’，随后在公平秤证据面前表示愿意退差。",
      "这段现场实拍被转发后，市场管理方回应已对涉事摊位进行计量抽查，并承诺加强日常巡查。不少市民点赞顾客‘较真’的做法，认为公共监督是维护公平买卖最有效的方式。",
    ],
  },
  {
    slug: "restaurant-service-20260807",
    title: "餐厅服务态度争议 现场录音还原经过",
    category: "dialogue",
    categoryName: "对话录音",
    duration: "05:36",
    views: 419000,
    time: "2026-08-07",
    location: "滨江路 · 某连锁餐厅",
    thumb: "https://readdy.ai/api/search-image?query=modern%20restaurant%20interior%20at%20night%20with%20a%20customer%20and%20a%20server%20in%20a%20heated%20exchange%20near%20the%20counter%2C%20warm%20dining%20lights%2C%20other%20diners%20watching%2C%20documentary%20photography%2C%20cinematic&width=640&height=360&seq=ev-restaurant-10&orientation=landscape",
    tags: ["餐厅", "服务", "对话录音"],
    summary: "顾客因菜品上错与服务员发生不快，双方现场对话被录下。视频发出后，关于‘服务行业该被无限包容吗’的话题迅速升温。",
    content: [
      "滨江路一家连锁餐厅内，顾客点的菜被服务员端错桌，等待期间又未能及时上齐，顾客情绪逐渐不满，与服务员就‘到底谁的责任’发生对话。服务员表示‘订单太多忙不过来’，顾客则质疑‘这不是借口’。",
      "现场录音在社交平台传播后，讨论两极分化：有人认为顾客太较真，也有人认为服务品质就是细节决定的。餐厅次日发文致歉，并为当桌顾客免单，同时宣布优化出餐流程。",
    ],
  },
  {
    slug: "neighbor-interview-20260806",
    title: "街头随机采访：邻里矛盾因何而起",
    category: "collection",
    categoryName: "事件合集",
    duration: "09:20",
    views: 601000,
    time: "2026-08-06",
    location: "老城区 · 街心公园",
    thumb: "https://readdy.ai/api/search-image?query=street%20interview%20scene%20at%20an%20old%20town%20park%20with%20a%20reporter%20holding%20a%20microphone%20talking%20to%20local%20residents%2C%20warm%20afternoon%20light%2C%20trees%20and%20benches%2C%20documentary%20style%2C%20cinematic&width=640&height=360&seq=ev-interview-11&orientation=landscape",
    tags: ["邻里", "街采", "事件合集"],
    summary: "走访老城区街头，用镜头和话筒收集居民眼中最常见的邻里矛盾：噪音、占道、宠物、漏水。一件件琐事背后，是城市公共生活的真实切片。",
    content: [
      "我们走上街头，随机采访了二十多位老城区居民，请他们聊聊印象最深的邻里矛盾。答案五花八门，但集中在几类：楼上楼下的噪音、楼道堆物占道、宠物不牵绳，以及公共区域漏水维修的推诿。",
      "受访者普遍认为，大部分矛盾并非‘对错分明’，而是缺乏有效沟通渠道。一位社区工作者在采访中建议，邻里之间先打个招呼、说句软话，多数纠纷都能在萌芽阶段化解。这条街采合集，也是想提醒大家：多一点换位思考，日子才更顺心。",
    ],
  },
  {
    slug: "concert-exit-20260805",
    title: "演唱会散场拥挤现场 全程记录提示安全",
    category: "record",
    categoryName: "全程记录",
    duration: "07:58",
    views: 823000,
    time: "2026-08-05",
    location: "市体育中心 · 外广场",
    thumb: "https://readdy.ai/api/search-image?query=huge%20crowd%20exiting%20a%20stadium%20concert%20at%20night%20flooding%20into%20an%20open%20plaza%2C%20stage%20lights%20in%20background%2C%20massive%20swaying%20crowd%2C%20security%20staff%20guiding%2C%20aerial%20long%20shot%2C%20documentary%20photography%2C%20cinematic&width=640&height=360&seq=ev-concert-12&orientation=landscape",
    tags: ["演唱会", "散场", "全程记录"],
    summary: "一场大型演唱会散场后，数万人集中涌向地铁与出口，现场一度出现拥挤。一段全程记录拍下了人流疏导的紧张过程，也敲响大型活动安全管理警钟。",
    content: [
      "演出结束后，数万名观众几乎同一时间涌出体育中心，通往地铁口与停车场的通道瞬间进入高负荷状态。航拍镜头显示，广场上人潮涌动，部分区域出现短暂停滞与推挤。",
      "现场安保与志愿者通过扩音器反复引导、分段放行，约半小时后人群逐步疏散完毕，未发生人员受伤。主办方事后回应，将优化出入口分流方案。这段全程记录也再次提醒：大型活动的安全，靠的是每一个环节的周密预案。",
    ],
  },
];

export const faqs = [
  {
    q: "吃瓜视频在线的内容来源是什么？",
    a: "平台聚合全网热点事件的现场实拍与路人记录，涵盖现场实拍、事件合集、路人视角、对话录音与全程记录五大主题分类。所有内容均按真实事件脉络整理，尽量保留原始画面与对话，帮助用户第一时间掌握事件全貌。",
  },
  {
    q: "视频为什么看起来像是手机随手拍的？",
    a: "这正是平台的核心定位。我们坚持用路人视角还原第一现场，不追求刻意构图与美化剪辑，而是以‘无剪辑、原汁原味’的方式呈现，确保事件真相不被滤镜和后期包装掩盖。",
  },
  {
    q: "如何按主题查找我想要的事件？",
    a: "您可以通过顶部的分类导航，进入‘现场实拍’‘事件合集’‘路人视角’‘对话录音’‘全程记录’等分类页，也可以使用首页与分类页的搜索框，输入事件关键词即可快速定位相关内容。",
  },
  {
    q: "平台如何保证内容的真实性？",
    a: "我们以原始画面与现场对话为依据，尽量呈现完整过程而非断章取义，并在整理时注明事件时间、地点与背景。同时坚持一事一还原、不夸大不煽动，为判断事件真相提供客观参照。",
  },
  {
    q: "看到争议事件，我该如何理性看待？",
    a: "建议先看完整记录再做判断，避免仅凭单一片段下结论。公共场所的纠纷往往各有立场，多方信息、完整语境才是接近真相的前提。平台倡导理性吃瓜、就事论事，不参与网络攻击与人身评判。",
  },
];

export const testimonials = [
  {
    name: "阿杰，27岁",
    role: "通勤上班族",
    text: "每天地铁通勤必刷现场实拍，视频短、信息全，地铁冲突那期我全程看完，评论区理性讨论很多，比单纯刷段子有营养多了。",
    rating: 5,
  },
  {
    name: "小林，31岁",
    role: "社区工作者",
    text: "处理邻里纠纷时会参考平台的事件合集和对话录音，很多现场处理方式给了我启发。把完整语境讲清楚，是这类内容最难能可贵的地方。",
    rating: 5,
  },
  {
    name: "老周，45岁",
    role: "自由摄影师",
    text: "最喜欢路人视角这个栏目，那种不摆拍的真实感很难得。平台按主题把散落的事件归类，找资料特别方便。",
    rating: 4,
  },
];